import {
  CSSProperties,
  FormEvent,
  KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Alert from '@amzn/meridian/alert';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import caretDownIconTokens from '@amzn/meridian-tokens/base/icon/caret-down';
import caretRightIconTokens from '@amzn/meridian-tokens/base/icon/caret-right';
import alertIconTokens from '@amzn/meridian-tokens/base/icon/alert';
import checkSmallIconTokens from '@amzn/meridian-tokens/base/icon/check-small';
import closeCircleIconTokens from '@amzn/meridian-tokens/base/icon/close-circle';
import closeIconTokens from '@amzn/meridian-tokens/base/icon/close-small';
import sendIconTokens from '@amzn/meridian-tokens/base/icon/send-small';
import stopIconTokens from '@amzn/meridian-tokens/base/icon/stop';
import arrowRightIconTokens from '@amzn/meridian-tokens/base/icon/arrow-right-small';
import sparklesIconTokens from '@amzn/meridian-tokens/base/icon/sparkles-small';
import { css, keyframes } from '@emotion/css';
import { Streamdown } from 'streamdown';
import 'streamdown/styles.css';
import useSurfaceStyles from '../hooks/useSurfaceStyles';
import IconButton from './IconButton';
import { useSettings } from '../providers/SettingsProvider';
import fontStacks from '../theme/font-stacks';
import { createTransition, fadeUpEntrance, motionDurations, reducedMotionQuery, staggerDelay } from '../theme/motion';
import { AgentStreamEvent, streamAgentResponse } from '../utils/agent-stream';

const streamingTextAnimation = {
  animation: 'fadeIn',
  duration: 160,
  sep: 'word',
  stagger: 0,
} as const;

// Reveal buffered text in small, uniform, word-aligned slices at a fast cadence so the fades form
// a smooth continuous wave rather than large blocks popping in. The slice size scales with the
// buffer so a fast burst is drained over roughly this many ticks instead of lagging behind.
const STREAM_REVEAL_INTERVAL_MS = 30;
const STREAM_REVEAL_TARGET_TICKS = 10;

function computeRevealSliceLength(bufferedText: string): number {
  const proportionalLength = Math.ceil(bufferedText.length / STREAM_REVEAL_TARGET_TICKS);
  let sliceEnd = Math.max(1, proportionalLength);

  // Extend to the end of the current word so a slice never splits a word mid-fade.
  while (sliceEnd < bufferedText.length && !/\s/.test(bufferedText[sliceEnd])) {
    sliceEnd += 1;
  }
  if (sliceEnd < bufferedText.length) {
    sliceEnd += 1;
  }

  return Math.min(sliceEnd, bufferedText.length);
}

const sidebarClass = css({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  maxWidth: 'calc(100vw - 40px)',
  height: '100%',
  minHeight: 0,
  color: 'var(--chat-ink-default)',
  fontFamily: fontStacks.sans,
});

// The composer floats over the bottom of the transcript, so the transcript reserves its measured
// height as padding and masks its own edges. Scrolled text fades out behind the composer instead of
// being clipped at a hard line, and the sidebar's gradient background stays visible through the fade.
const COMPOSER_FADE_DISTANCE_PX = 32;
const HEADER_FADE_DISTANCE_PX = 12;
const DEFAULT_COMPOSER_HEIGHT_PX = 76;

const messagesFadeMask = `linear-gradient(to bottom, transparent 0, #000 ${HEADER_FADE_DISTANCE_PX}px, #000 calc(100% - var(--chat-composer-height) - ${COMPOSER_FADE_DISTANCE_PX}px), transparent calc(100% - var(--chat-composer-height) + 8px))`;

const messagesClass = css({
  '--chat-composer-height': `${DEFAULT_COMPOSER_HEIGHT_PX}px`,
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowAnchor: 'none',
  overscrollBehavior: 'contain',
  padding: `16px 16px calc(var(--chat-composer-height) + 16px)`,
  maskImage: messagesFadeMask,
  WebkitMaskImage: messagesFadeMask,
});

const messageListClass = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 32,
  minHeight: '100%',
});

const userMessageRowClass = css({
  display: 'flex',
  justifyContent: 'flex-end',
});

const messageEntranceClass = css({
  animation: fadeUpEntrance,
  [reducedMotionQuery]: { animation: 'none' },
});

const userMessageClass = css({
  maxWidth: '90%',
  padding: '10px 14px',
  backgroundColor: 'var(--chat-accent-tint)',
  borderRadius: '16px 16px 4px 16px',
});

const userMessageTextClass = css({
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
});

const assistantMessageClass = css({
  width: '100%',
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  fontFamily: fontStacks.sans,
  fontSize: 14,
  lineHeight: 1.5,
  '& a': {
    color: 'var(--chat-link)',
    textDecoration: 'underline',
    overflowWrap: 'anywhere',
  },
  '& a:hover': {
    textDecoration: 'none',
  },
  '& h1': {
    margin: '20px 0 8px',
    fontSize: 20,
    lineHeight: '28px',
  },
  '& h2': {
    margin: '18px 0 8px',
    fontSize: 18,
    lineHeight: '24px',
  },
  '& h3': {
    margin: '16px 0 8px',
    fontSize: 16,
    lineHeight: '20px',
  },
  '& h4, & h5, & h6': {
    margin: '14px 0 8px',
    fontSize: 14,
    lineHeight: '20px',
  },
  '& [data-streamdown="table-wrapper"]': {
    margin: '16px 0',
    padding: 8,
    border: '1px solid var(--chat-border-default)',
    borderRadius: 8,
    backgroundColor: 'var(--chat-page-highlight)',
  },
  '& [data-streamdown="table-wrapper"] > div:first-child': {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 4,
    marginBottom: 8,
  },
  '& [data-streamdown="table-wrapper"] > div:first-child > div': {
    position: 'relative',
  },
  '& [data-streamdown="table-wrapper"] > div:first-child button, & [data-streamdown="code-block-actions"] button': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    padding: 0,
    border: 0,
    borderRadius: 4,
    backgroundColor: 'transparent',
    color: 'var(--chat-ink-subdued)',
    cursor: 'pointer',
  },
  '& [data-streamdown="table-wrapper"] > div:first-child button:hover, & [data-streamdown="code-block-actions"] button:hover':
    {
      backgroundColor: 'var(--chat-action-hover)',
    },
  '& [data-streamdown="table-wrapper"] > div:first-child > div > div': {
    position: 'absolute',
    top: 32,
    right: 0,
    zIndex: 20,
    minWidth: 128,
    overflow: 'hidden',
    border: '1px solid var(--chat-border-default)',
    borderRadius: 4,
    backgroundColor: 'var(--chat-page-default)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  '& [data-streamdown="table-wrapper"] > div:first-child > div > div button': {
    justifyContent: 'flex-start',
    width: '100%',
    height: 'auto',
    padding: '8px 12px',
    borderRadius: 0,
    color: 'var(--chat-ink-default)',
    fontFamily: 'inherit',
    fontSize: 14,
    whiteSpace: 'nowrap',
  },
  '& [data-streamdown="table-wrapper"] > div:last-child': {
    overflowX: 'auto',
    border: '1px solid var(--chat-border-default)',
    borderRadius: 4,
    backgroundColor: 'var(--chat-page-default)',
  },
  '& [data-streamdown="table"]': {
    width: '100%',
    borderCollapse: 'collapse',
  },
  '& [data-streamdown="table-header"]': {
    backgroundColor: 'var(--chat-page-highlight)',
  },
  '& [data-streamdown="table-header-cell"], & [data-streamdown="table-cell"]': {
    padding: '8px 12px',
    borderBottom: '1px solid var(--chat-border-subdued)',
    textAlign: 'left',
    verticalAlign: 'top',
  },
  '& [data-streamdown="table-header-cell"]': {
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  '& [data-streamdown="code-block"]': {
    position: 'relative',
    margin: '16px 0',
    padding: 8,
    border: '1px solid var(--chat-border-default)',
    borderRadius: 8,
    backgroundColor: 'var(--chat-page-highlight)',
  },
  '& [data-streamdown="code-block-header"]': {
    display: 'flex',
    alignItems: 'center',
    minHeight: 28,
    color: 'var(--chat-ink-subdued)',
    fontFamily: fontStacks.monospace,
    fontSize: 12,
  },
  '& [data-streamdown="code-block-actions"]': {
    position: 'absolute',
    top: 8,
    right: 8,
    display: 'flex',
    gap: 4,
  },
  '& [data-streamdown="code-block-body"]': {
    overflowX: 'auto',
    padding: 12,
    borderRadius: 4,
    backgroundColor: 'var(--chat-page-default)',
    fontFamily: fontStacks.monospace,
    fontSize: 13,
  },
  '& [data-streamdown="code-block-body"] pre': {
    margin: 0,
  },
  '& [data-streamdown="inline-code"]': {
    padding: '1px 4px',
    borderRadius: 3,
    backgroundColor: 'var(--chat-action-selected)',
    fontFamily: fontStacks.monospace,
    fontSize: 13,
  },
});

const assistantLoadingClass = css({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  minHeight: 32,
});

const typingDotKeyframes = keyframes({
  '0%, 80%, 100%': { opacity: 0.25, transform: 'translateY(0)' },
  '40%': { opacity: 1, transform: 'translateY(-3px)' },
});

const TYPING_DOT_COUNT = 3;
const TYPING_DOT_DELAY_MS = 160;

const typingDotClass = css({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: 'var(--chat-ink-subdued)',
  animation: `${typingDotKeyframes} 1.2s ease-in-out infinite`,
  [reducedMotionQuery]: { animation: 'none', opacity: 0.6 },
});

const toolActivityClass = css({
  marginBottom: 8,
});

const toolPillClass = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  width: 'auto',
  maxWidth: '100%',
  height: 30,
  minWidth: 0,
  overflow: 'hidden',
  padding: '0 10px',
  border: '1px solid var(--chat-border-formfield)',
  borderRadius: 999,
  backgroundColor: 'var(--chat-page-highlight)',
  color: 'var(--chat-ink-default)',
  fontFamily: 'inherit',
  fontSize: 12,
  cursor: 'pointer',
  transition: createTransition(['background-color', 'border-color']),
  '&:hover': {
    backgroundColor: 'var(--chat-action-hover)',
  },
  '&:focus-visible': {
    outline: '2px solid var(--chat-border-focus)',
    outlineOffset: 2,
  },
});

const completedToolPillClass = css({
  borderColor: 'var(--chat-support-border-success)',
  backgroundColor: 'var(--chat-support-fill-success)',
  '&:hover': {
    backgroundColor: 'var(--chat-support-fill-success)',
  },
});

const erroredToolPillClass = css({
  borderColor: 'var(--chat-support-border-error)',
  backgroundColor: 'var(--chat-support-fill-error)',
  '&:hover': {
    backgroundColor: 'var(--chat-support-fill-error)',
  },
});

const toolStatusClass = css({
  display: 'flex',
  flex: '0 0 16px',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
});

const incompleteToolPillClass = css({
  borderColor: 'var(--chat-support-border-warning)',
  backgroundColor: 'var(--chat-support-fill-warning)',
  '&:hover': {
    backgroundColor: 'var(--chat-support-fill-warning)',
  },
});

const toolStatusSuccessClass = css({
  color: 'var(--chat-support-ink-success)',
});

const toolStatusErrorClass = css({
  color: 'var(--chat-support-ink-error)',
});

const toolStatusWarningClass = css({
  color: 'var(--chat-support-ink-warning)',
});

const toolNameClass = css({
  flex: '0 1 auto',
  minWidth: 0,
  maxWidth: '45%',
  overflow: 'hidden',
  fontWeight: 700,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const toolInputSummaryClass = css({
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  color: 'var(--chat-ink-subdued)',
  fontFamily: fontStacks.monospace,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const toolExpansionClass = css({
  display: 'flex',
  flex: '0 0 12px',
  alignItems: 'center',
  justifyContent: 'center',
  width: 12,
  height: 12,
  color: 'var(--chat-ink-subdued)',
});

const toolInputClass = css({
  maxHeight: 240,
  margin: '6px 0 0',
  overflow: 'auto',
  padding: 8,
  borderRadius: 4,
  backgroundColor: 'var(--chat-page-highlight)',
  fontFamily: fontStacks.monospace,
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
});

const toolSectionLabelClass = css({
  margin: '8px 0 2px',
  color: 'var(--chat-ink-subdued)',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
});

const thinkingSectionClass = css({
  marginBottom: 8,
});

const thinkingToggleClass = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 0',
  border: 0,
  backgroundColor: 'transparent',
  color: 'var(--chat-ink-subdued)',
  fontFamily: 'inherit',
  fontSize: 12,
  cursor: 'pointer',
  transition: createTransition(['color']),
  '&:hover': {
    color: 'var(--chat-ink-default)',
  },
});

const thinkingBodyClass = css({
  margin: '6px 0 0',
  paddingLeft: 10,
  borderLeft: '2px solid var(--chat-border-subdued)',
  color: 'var(--chat-ink-subdued)',
  fontSize: 13,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
});

const emptyStateClass = css({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 24,
  textAlign: 'center',
});

const emptyStateIntroClass = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
});

const suggestionListClass = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  width: '100%',
  maxWidth: 340,
});

const suggestionArrowClass = css({
  display: 'flex',
  flex: '0 0 auto',
  color: 'var(--chat-ink-subdued)',
  opacity: 0,
  transform: 'translateX(-4px)',
  transition: createTransition(['opacity', 'transform', 'color']),
});

const suggestionItemClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  width: '100%',
  padding: '12px 14px',
  border: '1px solid var(--chat-border-subdued)',
  borderRadius: 12,
  backgroundColor: 'var(--chat-surface)',
  boxShadow: '0 1px 2px rgba(2, 8, 14, 0.04)',
  color: 'var(--chat-ink-default)',
  fontFamily: 'inherit',
  fontSize: 14,
  lineHeight: 1.4,
  textAlign: 'left',
  cursor: 'pointer',
  animation: fadeUpEntrance,
  transition: createTransition(['border-color', 'background-color', 'transform']),
  '&:hover': {
    borderColor: 'var(--chat-border-surface)',
    transform: 'translateY(-1px)',
  },
  [`&:hover .${suggestionArrowClass}, &:focus-visible .${suggestionArrowClass}`]: {
    color: 'var(--chat-action-primary)',
    opacity: 1,
    transform: 'none',
  },
  '&:focus-visible': {
    outline: '2px solid var(--chat-border-focus)',
    outlineOffset: 2,
  },
  [reducedMotionQuery]: { animation: 'none', transition: 'none', '&:hover': { transform: 'none' } },
});

const headerClass = css({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minHeight: 64,
  padding: '12px 12px 12px 20px',
  borderBottom: '1px solid var(--chat-border-subdued)',
});

const headerTitleClass = css({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  minWidth: 0,
});

const composerClass = css({
  position: 'absolute',
  right: 0,
  bottom: 0,
  left: 0,
  padding: '8px 16px 16px',
  pointerEvents: 'none',
  '& > *': {
    pointerEvents: 'auto',
  },
});

const composerBarClass = css({
  display: 'flex',
  alignItems: 'flex-end',
  gap: 8,
  padding: 8,
  border: '1px solid var(--chat-border-subdued)',
  borderRadius: 16,
  backgroundColor: 'var(--chat-surface)',
  boxShadow: '0 1px 2px rgba(2, 8, 14, 0.04), 0 8px 24px -8px rgba(2, 8, 14, 0.14)',
  transition: createTransition(['border-color', 'box-shadow']),
  '&:focus-within': {
    borderColor: 'var(--chat-action-primary)',
    boxShadow: '0 0 0 3px var(--chat-accent-ring), 0 8px 24px -8px rgba(2, 8, 14, 0.18)',
  },
});

const composerTextareaClass = css({
  flex: '1 1 auto',
  minWidth: 0,
  maxHeight: 500,
  margin: 0,
  boxSizing: 'border-box',
  padding: '7px 4px 7px 10px',
  border: 'none',
  outline: 'none',
  backgroundColor: 'transparent',
  color: 'var(--chat-ink-default)',
  fontFamily: 'inherit',
  fontSize: 14,
  lineHeight: 1.5,
  resize: 'none',
  overflowY: 'auto',
  '&::placeholder': {
    color: 'var(--chat-ink-subdued)',
  },
  '&:disabled': {
    cursor: 'not-allowed',
  },
});

const composerButtonClass = css({
  display: 'flex',
  flex: '0 0 auto',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  padding: 0,
  border: 0,
  borderRadius: 10,
  backgroundColor: 'var(--chat-action-primary)',
  color: 'var(--chat-action-primary-ink)',
  cursor: 'pointer',
  transition: createTransition(['background-color', 'color', 'transform'], motionDurations.instant),
  '&:hover': {
    backgroundColor: 'var(--chat-action-primary-hover)',
  },
  '&:active:not(:disabled)': {
    transform: 'scale(0.94)',
  },
  '&:focus-visible': {
    outline: '2px solid var(--chat-border-focus)',
    outlineOffset: 2,
  },
  '&:disabled': {
    backgroundColor: 'var(--chat-action-selected)',
    color: 'var(--chat-ink-subdued)',
    cursor: 'not-allowed',
  },
});

const resizeHandleClass = css({
  position: 'absolute',
  top: 0,
  left: 0,
  width: 8,
  height: '100%',
  cursor: 'col-resize',
  touchAction: 'none',
  zIndex: 1,
  '&:hover::after, &:focus-visible::after': {
    backgroundColor: 'var(--chat-border-focus)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 3,
    width: 2,
    height: '100%',
    backgroundColor: 'transparent',
  },
});

const MIN_SIDEBAR_WIDTH = 320;
const MAX_SIDEBAR_WIDTH = 720;
const DEFAULT_SIDEBAR_WIDTH = 400;

const SUGGESTED_QUESTIONS = [
  'Tell me about a service and its dependencies',
  'How does one service use another service?',
  'What does this service do?',
];

type MessageRole = 'user' | 'assistant';

type AssistantSegment =
  { kind: 'text'; text: string } | { kind: 'thinking'; text: string } | { kind: 'tool'; toolCall: AgentToolCall };

interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  segments?: AssistantSegment[];
  notice?: string;
}

interface AgentToolCall {
  id: string;
  name: string;
  input: string;
  output?: string;
  status: 'Preparing' | 'Running' | 'Complete' | 'Error' | 'Incomplete';
}

interface AgentChatSidebarProps {
  onClose: () => void;
}

function createUniqueId(): string {
  return globalThis.crypto.randomUUID();
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function formatToolInput(input: string): string {
  if (!input) {
    return 'No input provided';
  }

  try {
    const parsedInput: unknown = JSON.parse(input);
    if (!parsedInput || typeof parsedInput !== 'object' || Array.isArray(parsedInput)) {
      return JSON.stringify(parsedInput, null, 2);
    }

    const formattedInput = JSON.stringify(parsedInput, null, 2);
    if (formattedInput === '{}') {
      return 'No input provided';
    }

    return formattedInput
      .split('\n')
      .slice(1, -1)
      .map((line) => line.slice(2))
      .join('\n');
  } catch {
    return input;
  }
}

function summarizeToolInput(input: string): string {
  if (!input) {
    return '';
  }

  try {
    return JSON.stringify(JSON.parse(input));
  } catch {
    return input.replace(/\s+/g, ' ').trim();
  }
}

// Newer Claude models default to omitted thinking display, which streams thinking blocks with empty
// text. Hiding those keeps an empty "Thinking" toggle out of the transcript and lets the loader show
// while the model reasons silently.
function isVisibleSegment(segment: AssistantSegment): boolean {
  return segment.kind !== 'thinking' || segment.text.trim().length > 0;
}

function appendTextSegment(segments: AssistantSegment[], kind: 'text' | 'thinking', text: string): AssistantSegment[] {
  const lastSegment = segments[segments.length - 1];
  if (lastSegment && lastSegment.kind === kind) {
    return [...segments.slice(0, -1), { kind, text: lastSegment.text + text }];
  }
  return [...segments, { kind, text }];
}

function updateToolSegment(
  segments: AssistantSegment[],
  toolUseId: string,
  update: Partial<AgentToolCall>,
): AssistantSegment[] {
  return segments.map((segment) =>
    segment.kind === 'tool' && segment.toolCall.id === toolUseId
      ? { ...segment, toolCall: { ...segment.toolCall, ...update } }
      : segment,
  );
}

function reduceSegments(
  segments: AssistantSegment[],
  streamEvent: AgentStreamEvent,
  activeToolUseId: string | null,
): AssistantSegment[] {
  switch (streamEvent.type) {
    case 'text':
      return appendTextSegment(segments, 'text', streamEvent.content);
    case 'thinking':
      return appendTextSegment(segments, 'thinking', streamEvent.content);
    case 'tool-start':
      return [
        ...segments,
        {
          kind: 'tool',
          toolCall: { id: streamEvent.toolUseId, name: streamEvent.name, input: '', status: 'Preparing' },
        },
      ];
    case 'tool-input':
      return activeToolUseId
        ? segments.map((segment) =>
            segment.kind === 'tool' && segment.toolCall.id === activeToolUseId
              ? {
                  ...segment,
                  toolCall: { ...segment.toolCall, input: `${segment.toolCall.input}${streamEvent.content}` },
                }
              : segment,
          )
        : segments;
    case 'tool-stop':
      return activeToolUseId ? updateToolSegment(segments, activeToolUseId, { status: 'Running' }) : segments;
    case 'tool-ready':
      return updateToolSegment(segments, streamEvent.toolUseId, {
        input: JSON.stringify(streamEvent.input ?? {}),
        status: 'Running',
      });
    case 'tool-complete':
      return updateToolSegment(segments, streamEvent.toolUseId, {
        status: streamEvent.status === 'error' ? 'Error' : 'Complete',
        output: streamEvent.output,
      });
    default:
      return segments;
  }
}

function ToolActivity({ toolCall }: { toolCall: AgentToolCall }) {
  const [isOpen, setIsOpen] = useState(false);
  const isComplete = toolCall.status === 'Complete';
  const isError = toolCall.status === 'Error';
  const isIncomplete = toolCall.status === 'Incomplete';
  const inputSummary = summarizeToolInput(toolCall.input);

  const pillStatusClass = isError
    ? erroredToolPillClass
    : isIncomplete
      ? incompleteToolPillClass
      : isComplete
        ? completedToolPillClass
        : '';

  return (
    <div className={toolActivityClass}>
      <button
        type="button"
        className={`${toolPillClass} ${pillStatusClass}`}
        aria-expanded={isOpen}
        aria-label={`${toolCall.name}, ${toolCall.status}`}
        title={inputSummary || toolCall.name}
        onClick={() => setIsOpen((currentIsOpen) => !currentIsOpen)}
      >
        {isError ? (
          <span className={`${toolStatusClass} ${toolStatusErrorClass}`}>
            <Icon tokens={closeCircleIconTokens} />
          </span>
        ) : isIncomplete ? (
          <span className={`${toolStatusClass} ${toolStatusWarningClass}`}>
            <Icon tokens={alertIconTokens} />
          </span>
        ) : isComplete ? (
          <span className={`${toolStatusClass} ${toolStatusSuccessClass}`}>
            <Icon tokens={checkSmallIconTokens} />
          </span>
        ) : (
          <span className={toolStatusClass}>
            <Loader size="small" label={`${toolCall.name} is running`} />
          </span>
        )}
        <span className={toolNameClass}>{toolCall.name}</span>
        {inputSummary && <span className={toolInputSummaryClass}>{inputSummary}</span>}
        <span className={toolExpansionClass}>
          <Icon tokens={isOpen ? caretDownIconTokens : caretRightIconTokens} color="secondary" />
        </span>
      </button>
      {isOpen && (
        <>
          <div className={toolSectionLabelClass}>Input</div>
          <pre className={toolInputClass}>{formatToolInput(toolCall.input)}</pre>
          {toolCall.output && (
            <>
              <div className={toolSectionLabelClass}>{isError ? 'Error' : 'Output'}</div>
              <pre className={toolInputClass}>{toolCall.output}</pre>
            </>
          )}
        </>
      )}
    </div>
  );
}

function ThinkingActivity({ thinking, defaultOpen }: { thinking: string; defaultOpen: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // Re-sync only when defaultOpen transitions (auto-open while streaming, auto-collapse once
  // it is no longer the active segment) without clobbering a manual toggle in between.
  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen]);

  return (
    <div className={thinkingSectionClass}>
      <button
        type="button"
        className={thinkingToggleClass}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((currentIsOpen) => !currentIsOpen)}
      >
        <Icon tokens={isOpen ? caretDownIconTokens : caretRightIconTokens} color="secondary" />
        Thinking
      </button>
      {isOpen && <div className={thinkingBodyClass}>{thinking}</div>}
    </div>
  );
}

export default function AgentChatSidebar({ onClose }: AgentChatSidebarProps) {
  const { settings } = useSettings();
  const meridianTheme = useMeridianTheme();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageDraft, setMessageDraft] = useState('');
  const [sessionId] = useState(createUniqueId);
  const [isStreaming, setIsStreaming] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const abortControllerRef = useRef<AbortController | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const streamRevealTimerRef = useRef<number | null>(null);
  const shouldFollowLatestRef = useRef(true);
  const resizeDragStartRef = useRef<{ pointerX: number; width: number } | null>(null);
  const composerTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const composerFormRef = useRef<HTMLFormElement | null>(null);
  const { accentIconClass, accentPillClass, accentTintColor, accentRingColor } = useSurfaceStyles();
  const sidebarThemeStyles = {
    backgroundImage: meridianTheme('appSidebarBackground'),
    '--chat-accent-tint': accentTintColor,
    '--chat-accent-ring': accentRingColor,
    '--chat-surface': meridianTheme('themeSurfaceDefault'),
    '--chat-border-surface': meridianTheme('themeBorderSurface'),
    '--chat-action-hover': meridianTheme('themeActionNeutralHover'),
    '--chat-action-selected': meridianTheme('themeActionNeutralSelected'),
    '--chat-border-default': meridianTheme('themeBorderDefault'),
    '--chat-border-focus': meridianTheme('themeBorderFocus'),
    '--chat-border-formfield': meridianTheme('themeBorderFormfield'),
    '--chat-border-subdued': meridianTheme('themeBorderSubdued'),
    '--chat-link': meridianTheme('themeActionPrimaryDefault'),
    '--chat-action-primary': meridianTheme('themeActionPrimaryDefault'),
    '--chat-action-primary-hover': meridianTheme('themeActionPrimaryHover'),
    '--chat-action-primary-ink': meridianTheme('themeInkInverse'),
    '--chat-ink-default': meridianTheme('themeInkDefault'),
    '--chat-ink-subdued': meridianTheme('themeInkSubdued'),
    '--chat-page-default': meridianTheme('themePageDefault'),
    '--chat-page-highlight': meridianTheme('themePageHighlight'),
    '--chat-support-border-success': meridianTheme('themeSupportBorderSuccess'),
    '--chat-support-fill-success': meridianTheme('themeSupportFillSuccess'),
    '--chat-support-ink-success': meridianTheme('themeSupportInkSuccess'),
    '--chat-support-border-error': meridianTheme('themeSupportBorderError'),
    '--chat-support-fill-error': meridianTheme('themeSupportFillError'),
    '--chat-support-ink-error': meridianTheme('themeSupportInkError'),
    '--chat-support-border-warning': meridianTheme('themeSupportBorderWarning'),
    '--chat-support-fill-warning': meridianTheme('themeSupportFillWarning'),
    '--chat-support-ink-warning': meridianTheme('themeSupportInkWarning'),
  } as CSSProperties;

  // Keep the latest content in view while streaming. Runs before paint so appended words never
  // flash above the fold, and only follows while the user is already parked near the bottom.
  useLayoutEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer || !shouldFollowLatestRef.current) {
      return;
    }
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }, [errorMessage, isStreaming, messages]);

  // Auto-grow the composer from its content up to the CSS max height. Done in JS rather than relying
  // on CSS field-sizing, which is Chromium-only, so the textarea also expands in Firefox and Safari.
  useLayoutEffect(() => {
    const composerTextarea = composerTextareaRef.current;
    if (!composerTextarea) {
      return;
    }
    composerTextarea.style.height = 'auto';
    composerTextarea.style.height = `${composerTextarea.scrollHeight}px`;
  }, [messageDraft]);

  // Follow height changes that happen without a React commit, such as the final assistant block
  // reflowing when Streamdown finalizes its markdown or the composer growing with its draft. A
  // dependency-based effect cannot catch these. The composer height is written straight to the
  // transcript's CSS variable so the reserved space and fade track it without re-rendering.
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    const messageList = messageListRef.current;
    const composerForm = composerFormRef.current;
    if (!messagesContainer || !messageList || !composerForm) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      messagesContainer.style.setProperty('--chat-composer-height', `${composerForm.offsetHeight}px`);
      if (shouldFollowLatestRef.current) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    });
    resizeObserver.observe(messageList);
    resizeObserver.observe(composerForm);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (streamRevealTimerRef.current !== null) {
        clearTimeout(streamRevealTimerRef.current);
      }
    };
  }, []);

  const handleMessagesScroll = () => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) {
      return;
    }

    // Re-pin whenever the viewport is near the bottom (including our own auto-scroll landing there)
    // and release the pin as soon as the user scrolls further up than that threshold.
    const distanceFromBottom =
      messagesContainer.scrollHeight - messagesContainer.clientHeight - messagesContainer.scrollTop;
    shouldFollowLatestRef.current = distanceFromBottom <= 40;
  };

  const removeEmptyAssistantMessage = (assistantMessageId: string) => {
    setMessages((currentMessages) =>
      currentMessages.filter(
        (message) => message.id !== assistantMessageId || message.segments?.some(isVisibleSegment),
      ),
    );
  };

  const sendMessage = async () => {
    const prompt = messageDraft.trim();
    if (!prompt || isStreaming) {
      return;
    }

    if (!settings?.agentEndpoint) {
      setErrorMessage('The IRIS agent endpoint is not configured in settings.json.');
      return;
    }

    const userMessage: ChatMessage = { id: createUniqueId(), role: 'user', content: prompt };
    const assistantMessageId = createUniqueId();
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      segments: [],
    };
    const abortController = new AbortController();
    let streamedText = '';
    let streamedErrorMessage: string | null = null;
    let hasToolActivity = false;
    let activeToolUseId: string | null = null;

    abortControllerRef.current = abortController;
    shouldFollowLatestRef.current = true;
    setMessages((currentMessages) => [...currentMessages, userMessage, assistantMessage]);
    setMessageDraft('');
    setErrorMessage(null);
    setIsStreaming(true);

    let pendingRevealText = '';
    let streamComplete = false;
    let notifyRevealDrained: (() => void) | null = null;

    const commitRevealText = (text: string) => {
      setMessages((currentMessages) =>
        currentMessages.map((message) =>
          message.id === assistantMessageId
            ? { ...message, segments: reduceSegments(message.segments ?? [], { type: 'text', content: text }, null) }
            : message,
        ),
      );
    };

    // Drain the buffer one slice per fade interval, then either keep the cadence going while more
    // text is expected or wind down once the stream is finished and nothing is left to reveal.
    const revealNextSlice = () => {
      if (pendingRevealText) {
        const sliceLength = computeRevealSliceLength(pendingRevealText);
        const slice = pendingRevealText.slice(0, sliceLength);
        pendingRevealText = pendingRevealText.slice(sliceLength);
        commitRevealText(slice);
      }

      if (streamComplete && !pendingRevealText) {
        streamRevealTimerRef.current = null;
        (notifyRevealDrained as (() => void) | null)?.();
        return;
      }
      streamRevealTimerRef.current = window.setTimeout(revealNextSlice, STREAM_REVEAL_INTERVAL_MS);
    };

    const ensureRevealTimer = () => {
      if (streamRevealTimerRef.current === null) {
        streamRevealTimerRef.current = window.setTimeout(revealNextSlice, STREAM_REVEAL_INTERVAL_MS);
      }
    };

    const flushPendingRevealText = () => {
      if (streamRevealTimerRef.current !== null) {
        clearTimeout(streamRevealTimerRef.current);
        streamRevealTimerRef.current = null;
      }
      if (pendingRevealText) {
        const slice = pendingRevealText;
        pendingRevealText = '';
        commitRevealText(slice);
      }
    };

    try {
      await streamAgentResponse({
        endpoint: settings.agentEndpoint,
        prompt,
        sessionId,
        signal: abortController.signal,
        onEvent: (streamEvent: AgentStreamEvent) => {
          if (streamEvent.type === 'text') {
            streamedText += streamEvent.content;
            pendingRevealText += streamEvent.content;
            ensureRevealTimer();
            return;
          }

          // Non-text events reveal any buffered text immediately so tool and thinking segments stay ordered.
          flushPendingRevealText();

          if (streamEvent.type === 'error') {
            streamedErrorMessage = streamEvent.message;
          }

          if (streamEvent.type === 'tool-start') {
            hasToolActivity = true;
            activeToolUseId = streamEvent.toolUseId;
          }

          const eventToolUseId = activeToolUseId;
          if (
            streamEvent.type === 'tool-stop' ||
            (streamEvent.type === 'tool-ready' && streamEvent.toolUseId === activeToolUseId)
          ) {
            activeToolUseId = null;
          }

          setMessages((currentMessages) =>
            currentMessages.map((message) =>
              message.id === assistantMessageId
                ? { ...message, segments: reduceSegments(message.segments ?? [], streamEvent, eventToolUseId) }
                : message,
            ),
          );
        },
      });

      // Let the paced reveal drain the tail instead of dumping it all at once. If the loop already
      // idled out, resolve immediately; otherwise the next completed tick resolves this.
      streamComplete = true;
      await new Promise<void>((resolve) => {
        if (streamRevealTimerRef.current === null && !pendingRevealText) {
          resolve();
          return;
        }
        notifyRevealDrained = resolve;
      });

      if (streamedErrorMessage) {
        setErrorMessage(`The Discovery Agent reported an error: ${streamedErrorMessage}`);
        if (!streamedText && !hasToolActivity) {
          removeEmptyAssistantMessage(assistantMessageId);
        }
      } else if (!streamedText) {
        // Preserve useful tool activity without presenting the missing final response as a request failure.
        if (hasToolActivity) {
          setMessages((currentMessages) =>
            currentMessages.map((message) =>
              message.id === assistantMessageId
                ? { ...message, notice: 'The Discovery Agent did not return a final response.' }
                : message,
            ),
          );
        } else {
          removeEmptyAssistantMessage(assistantMessageId);
          setErrorMessage('The IRIS agent completed without returning a text response.');
        }
      }
    } catch (error) {
      if (!streamedText) {
        removeEmptyAssistantMessage(assistantMessageId);
      }

      if (isAbortError(error)) {
        return;
      }

      setErrorMessage(error instanceof Error ? error.message : 'The IRIS agent request failed.');
    } finally {
      // Stop the paced reveal and surface any text still buffered when the stream ends early.
      flushPendingRevealText();

      // Reconcile on every exit path (success, mid-stream error, abort) so any tool left in
      // Preparing/Running never renders a spinner forever.
      setMessages((currentMessages) =>
        currentMessages.map((message) =>
          message.id === assistantMessageId
            ? {
                ...message,
                segments: (message.segments ?? []).map((segment) =>
                  segment.kind === 'tool' &&
                  (segment.toolCall.status === 'Preparing' || segment.toolCall.status === 'Running')
                    ? { ...segment, toolCall: { ...segment.toolCall, status: 'Incomplete' } }
                    : segment,
                ),
              }
            : message,
        ),
      );
      abortControllerRef.current = null;
      setIsStreaming(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void sendMessage();
  };

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.target instanceof HTMLTextAreaElement && event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void sendMessage();
    }
  };

  const fillComposerWithSuggestion = (question: string) => {
    setMessageDraft(question);
    composerTextareaRef.current?.focus();
  };

  const stopStreaming = () => {
    abortControllerRef.current?.abort();
  };

  // Keep the stored width in sync with what is actually painted: the sidebar's CSS also caps it to
  // the viewport, so on narrow viewports clamp to the same bound or the drag baseline and announced
  // value would drift past the visible edge.
  const clampSidebarWidth = (width: number) =>
    Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, window.innerWidth - 40, width));

  // Pointer capture routes every move/up to the handle even when the cursor leaves it, and the
  // browser releases the capture automatically on unmount, so there are no window listeners to leak.
  const handleResizePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    resizeDragStartRef.current = { pointerX: event.clientX, width: sidebarWidth };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleResizePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const dragStart = resizeDragStartRef.current;
    if (!dragStart) {
      return;
    }
    // Sidebar is anchored to the right edge, so dragging the handle left widens it.
    setSidebarWidth(clampSidebarWidth(dragStart.width + (dragStart.pointerX - event.clientX)));
  };

  // Fires on every capture-end path (pointerup, pointercancel, gesture interruption), so the drag
  // state can never stay set and let a later stray move resume resizing.
  const handleResizeEnd = () => {
    resizeDragStartRef.current = null;
  };

  const handleResizeKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Sidebar is anchored to the right edge, so ArrowLeft widens it and ArrowRight narrows it.
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setSidebarWidth((width) => clampSidebarWidth(width + 16));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setSidebarWidth((width) => clampSidebarWidth(width - 16));
    }
  };

  return (
    <div className={sidebarClass} style={{ ...sidebarThemeStyles, width: sidebarWidth }}>
      <div
        className={resizeHandleClass}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize Discovery Agent"
        tabIndex={0}
        aria-valuenow={sidebarWidth}
        aria-valuemin={MIN_SIDEBAR_WIDTH}
        aria-valuemax={MAX_SIDEBAR_WIDTH}
        onPointerDown={handleResizePointerDown}
        onPointerMove={handleResizePointerMove}
        onLostPointerCapture={handleResizeEnd}
        onKeyDown={handleResizeKeyDown}
      />
      <div className={headerClass}>
        <span className={accentIconClass}>
          <Icon tokens={sparklesIconTokens} />
        </span>
        <div className={headerTitleClass}>
          <Row alignmentVertical="center" spacing="200">
            <Heading level={2} type="h200">
              Discovery Agent
            </Heading>
            <span className={accentPillClass}>Experimental</span>
          </Row>
          <Text type="b100" color="secondary" truncate>
            Ask about services and how they connect
          </Text>
        </div>
        <IconButton iconTokens={closeIconTokens} label="Close Discovery Agent" onClick={onClose} />
      </div>

      <div
        ref={messagesContainerRef}
        className={messagesClass}
        role="log"
        aria-live="polite"
        aria-label="Discovery Agent conversation"
        onScroll={handleMessagesScroll}
      >
        <div ref={messageListRef} className={messageListClass}>
          {messages.length === 0 && (
            <div className={emptyStateClass}>
              <div className={emptyStateIntroClass}>
                <span className={accentIconClass}>
                  <Icon tokens={sparklesIconTokens} />
                </span>
                <Heading level={3} type="h300" alignment="center">
                  How can I help?
                </Heading>
                <Text type="b200" color="secondary" alignment="center">
                  Pick a starting point or ask your own question.
                </Text>
              </div>
              <div className={suggestionListClass}>
                {SUGGESTED_QUESTIONS.map((question, position) => (
                  <button
                    key={question}
                    type="button"
                    className={suggestionItemClass}
                    style={{ animationDelay: staggerDelay(position + 1) }}
                    onClick={() => fillComposerWithSuggestion(question)}
                  >
                    {question}
                    <span className={suggestionArrowClass}>
                      <Icon tokens={arrowRightIconTokens} />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((message, messageIndex) => {
            const isActiveAssistantResponse =
              message.role === 'assistant' && isStreaming && messageIndex === messages.length - 1;

            if (message.role === 'user') {
              return (
                <div key={message.id} className={`${userMessageRowClass} ${messageEntranceClass}`}>
                  <div className={userMessageClass}>
                    <Text tag="div" type="b200" breakWord className={userMessageTextClass}>
                      {message.content}
                    </Text>
                  </div>
                </div>
              );
            }

            const segments = (message.segments ?? []).filter(isVisibleSegment);
            const lastSegmentIndex = segments.length - 1;

            return (
              <div key={message.id} className={messageEntranceClass}>
                {segments.map((segment, segmentIndex) => {
                  const isLastSegment = segmentIndex === lastSegmentIndex;

                  if (segment.kind === 'thinking') {
                    return (
                      <ThinkingActivity
                        key={segmentIndex}
                        thinking={segment.text}
                        defaultOpen={isActiveAssistantResponse && isLastSegment}
                      />
                    );
                  }

                  if (segment.kind === 'tool') {
                    return <ToolActivity key={segment.toolCall.id} toolCall={segment.toolCall} />;
                  }

                  return (
                    <Streamdown
                      key={segmentIndex}
                      animated={streamingTextAnimation}
                      className={assistantMessageClass}
                      isAnimating={isActiveAssistantResponse && isLastSegment}
                      linkSafety={{ enabled: false }}
                      mode={isActiveAssistantResponse && isLastSegment ? 'streaming' : 'static'}
                    >
                      {segment.text}
                    </Streamdown>
                  );
                })}
                {message.notice && (
                  <Text tag="div" type="b100" color="secondary">
                    {message.notice}
                  </Text>
                )}
                {segments.length === 0 && isActiveAssistantResponse && (
                  <div className={assistantLoadingClass} role="status" aria-label="Discovery Agent is responding">
                    {Array.from({ length: TYPING_DOT_COUNT }, (_, position) => (
                      <span
                        key={position}
                        className={typingDotClass}
                        style={{ animationDelay: `${position * TYPING_DOT_DELAY_MS}ms` }}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {errorMessage && (
            <Alert type="error" size="small">
              {errorMessage}
            </Alert>
          )}
        </div>
      </div>

      <form ref={composerFormRef} className={composerClass} onSubmit={handleSubmit} onKeyDown={handleComposerKeyDown}>
        <div className={composerBarClass}>
          <textarea
            ref={composerTextareaRef}
            className={composerTextareaClass}
            value={messageDraft}
            onChange={(event) => setMessageDraft(event.target.value)}
            placeholder="Ask a question"
            aria-label="Message Discovery Agent"
            spellCheck
            disabled={isStreaming}
            rows={1}
          />
          {isStreaming ? (
            <button type="button" className={composerButtonClass} onClick={stopStreaming} aria-label="Stop response">
              <Icon tokens={stopIconTokens}>Stop response</Icon>
            </button>
          ) : (
            <button
              type="submit"
              className={composerButtonClass}
              disabled={!messageDraft.trim()}
              aria-label="Send message"
            >
              <Icon tokens={sendIconTokens}>Send message</Icon>
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
